import React, { useState, useMemo, useRef } from 'react';
import {
  Box,
  Typography,
  Chip,
  useMediaQuery,
  useTheme,
  Link,
} from '@mui/material';
import Star from '@mui/icons-material/Star';
import ExpandMore from '@mui/icons-material/ExpandMore';
import ExpandLess from '@mui/icons-material/ExpandLess';
import { useTranslation } from 'react-i18next';
import SimpleBar from 'simplebar-react';
import 'simplebar-react/dist/simplebar.min.css';
import { AnimatedTabs } from '../../../../components/ui';
import ItemIcon from '../../../../components/ItemIcon';
import type { SkillDetailDto } from '../../../../types';
import {
  BG_COLORS,
  BORDER_COLORS,
  TEXT_COLORS,
} from '../../../../utils/colors';
import { WIKI_DETAIL, SCROLL_RESERVE } from '../../../../utils/responsive';
import { formatDuration } from '../../../../utils/formatDuration';

interface SkillDetailProps {
  skills: SkillDetailDto[]; // Array of all levels for this skill
}

// Helper functions for formatting
const formatNumber = (num?: number): string => {
  if (!num || num === 0) return '0';
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return num.toString();
};

const formatBoolean = (value: boolean | undefined, t: (key: string) => string): string => {
  return value ? t('booleanValues.yes') : t('booleanValues.no');
};

const SkillDetail: React.FC<SkillDetailProps> = ({ skills }) => {
  const { t } = useTranslation('wiki');
  const theme = useTheme();
  const isSm = useMediaQuery(theme.breakpoints.up('sm'));
  const [selectedLevelIndex, setSelectedLevelIndex] = useState(0);
  const [descExpanded, setDescExpanded] = useState(false);
  const scrollRef = useRef<any>(null);

  // Generate tabs with icons for AnimatedTabs (must be before early return)
  const levelTabs = useMemo(() => {
    if (!skills || skills.length === 0) return [];
    return skills.map((skill, index) => ({
      value: `level-${index}`,
      label: `${t('detailSections.level')} ${skill.level}`,
      icon: <Star />
    }));
  }, [skills, t]);

  const handleLevelChange = (value: string) => {
    const idx = levelTabs.findIndex(t => t.value === value);
    setSelectedLevelIndex(idx >= 0 ? idx : 0);
    // Reset scroll position
    if (scrollRef.current) {
      scrollRef.current.getScrollElement().scrollTop = 0;
    }
  };

  if (!skills || skills.length === 0) {
    return (
      <Box sx={{ p: 3, background: BG_COLORS.GLASS_DARKER, borderRadius: 2, textAlign: 'center' }}>
        <Typography color="error">{t('messages.itemDetailsError')}</Typography>
      </Box>
    );
  }

  const firstSkill = skills[0];
  const hasMultipleLevels = skills.length > 1;
  const currentSkill = skills[selectedLevelIndex] || firstSkill;

  // Filter out ID and other unwanted fields for a skill level
  const getSkillLevelProperties = (skill: SkillDetailDto) => {
    const properties: Array<{ label: string; value: any; type?: string }> = [];

    // Core Properties
    if (skill.level) properties.push({ label: t('skillProperties.level'), value: skill.level, type: 'stat' });
    if (skill.skill_type) properties.push({ label: t('skillProperties.skillType'), value: skill.skill_type, type: 'stat' });
    if (skill.operator_type && skill.operator_type !== 'NONE') properties.push({ label: t('skillProperties.type'), value: skill.operator_type, type: 'stat' });

    // Resource Consumption
    if (skill.mp_consume && skill.mp_consume > 0) properties.push({ label: t('skillProperties.mpConsume'), value: formatNumber(skill.mp_consume), type: 'cost' });
    if (skill.hp_consume && skill.hp_consume > 0) properties.push({ label: t('skillProperties.hpConsume'), value: formatNumber(skill.hp_consume), type: 'cost' });

    // Learning Costs
    if (skill.sp_cost && skill.sp_cost > 0) properties.push({ label: t('skillProperties.spCost'), value: formatNumber(skill.sp_cost), type: 'learning' });
    if (skill.adena_cost && skill.adena_cost > 0) properties.push({ label: t('skillProperties.adenaCost'), value: formatNumber(skill.adena_cost), type: 'learning' });
    if (skill.lvl_learn) properties.push({ label: t('skillProperties.learnLevel'), value: skill.lvl_learn, type: 'learning' });

    // Timing
    if (skill.reuse_delay && skill.reuse_delay > 0) properties.push({ label: t('skillProperties.cooldown'), value: formatDuration(skill.reuse_delay / 1000, t), type: 'timing' });
    if (skill.cast_range && skill.cast_range > 0) properties.push({ label: t('skillProperties.castRange'), value: skill.cast_range, type: 'timing' });
    if (skill.hit_time && skill.hit_time > 0) properties.push({ label: t('skillProperties.castTime'), value: formatDuration(skill.hit_time / 1000, t), type: 'timing' });
    if (skill.abnormal_time && skill.abnormal_time > 0) properties.push({ label: t('skillProperties.duration'), value: formatDuration(skill.abnormal_time, t), type: 'timing' });

    // Magic Properties
    if (skill.magic_level && skill.magic_level > 0) properties.push({ label: t('skillProperties.magicLevel'), value: skill.magic_level, type: 'stat' });

    // Skill Nature (buff/debuff)
    if (skill.skill_nature) {
      const natureLabel = skill.skill_nature === 'buff' ? t('skillProperties.buff') : skill.skill_nature === 'debuff' ? t('skillProperties.debuff') : null;
      if (natureLabel) properties.push({ label: t('skillProperties.nature'), value: natureLabel, type: 'stat' });
    }

    // Boolean Properties
    if (skill.is_auto_use !== undefined) properties.push({ label: t('skillProperties.selfEffect'), value: formatBoolean(skill.is_auto_use, t), type: 'boolean' });
    if (skill.auto_get !== undefined) properties.push({ label: t('skillProperties.learnable'), value: formatBoolean(skill.auto_get, t), type: 'boolean' });

    // Item Consumption
    if (skill.item_consume_id && skill.item_consume_id > 0) {
      properties.push({ label: t('skillProperties.requiredItem'), value: `ID: ${skill.item_consume_id}`, type: 'item' });
      if (skill.item_consume_count && skill.item_consume_count > 0) {
        properties.push({ label: t('skillProperties.itemCount'), value: skill.item_consume_count, type: 'item' });
      }
    }

    return properties;
  };

  const properties = getSkillLevelProperties(currentSkill);

  return (
    <Box sx={{
      flex: 1,
      minHeight: 0,
      display: 'flex',
      flexDirection: 'column',
    }}>
        {/* Header Section - Fixed */}
        <Box sx={{
          flexShrink: 0,
          display: 'flex',
          alignItems: 'flex-start',
          gap: { xs: 1.5, sm: 3 },
          mb: { xs: 1, sm: 3 }
        }}>
          <Box sx={{ flexShrink: 0 }}>
            <ItemIcon
              icon={currentSkill.icon_skill || ''}
              iconPanel={currentSkill.icon_panel || ''}
              name={currentSkill.name}
              size={isSm ? 64 : 40}
            />
          </Box>

          <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
            <Typography component="h1" sx={{ fontWeight: 700, mb: 0.5, fontSize: WIKI_DETAIL.titleFontSize }}>
              {currentSkill.name}
            </Typography>

            {currentSkill.description && (
              <Box>
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: WIKI_DETAIL.descriptionFontSize,
                    lineHeight: 1.4,
                    color: TEXT_COLORS.SOFT,
                    ...(!isSm && !descExpanded && {
                      display: '-webkit-box',
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                    }),
                  }}
                  dangerouslySetInnerHTML={{
                    __html: currentSkill.description.replace(/\\n/g, '<br/>')
                  }}
                />
                {!isSm && currentSkill.description.length > 100 && (
                  <Link
                    component="button"
                    variant="body2"
                    onClick={() => setDescExpanded(!descExpanded)}
                    sx={{
                      fontSize: '0.65rem',
                      color: '#ef4444',
                      textDecoration: 'none',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.25,
                      '&:hover': { opacity: 0.8 },
                    }}
                  >
                    {descExpanded ? <ExpandLess sx={{ fontSize: '0.85rem', mt: '-1px' }} /> : <ExpandMore sx={{ fontSize: '0.85rem', mt: '-1px' }} />}
                    {descExpanded ? 'Свернуть' : 'Развернуть'}
                  </Link>
                )}
              </Box>
            )}
          </Box>
        </Box>

        {/* Level Tabs - Fixed */}
        {hasMultipleLevels && (
          <Box sx={{ flexShrink: 0 }}>
            <AnimatedTabs
              value={levelTabs[selectedLevelIndex]?.value || 'level-0'}
              onChange={handleLevelChange}
              tabs={levelTabs}
            />
          </Box>
        )}

        {/* Scrollable Content */}
        <SimpleBar
          ref={scrollRef}
          style={{
            flex: 1,
            minHeight: 0,
            paddingRight: isSm ? SCROLL_RESERVE.sm : SCROLL_RESERVE.xs,
            paddingBottom: 16
          }}
          autoHide={false}
        >
          {/* Properties Section */}
        {properties.length > 0 && (
          <Box>
            {/* Stats Group */}
            {properties.filter(p => p.type === 'stat').length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: { xs: 1, sm: 1.5 }, fontWeight: 600, color: 'text.secondary', fontSize: WIKI_DETAIL.sectionTitleFontSize }}>
                  {t('detailSections.basicInfo')}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {properties.filter(p => p.type === 'stat').map((prop, index) => (
                    <Chip
                      key={index}
                      label={`${prop.label}: ${prop.value}`}
                      size="small"
                      sx={{
                        backgroundColor: BG_COLORS.GLASS,
                        color: 'text.primary',
                        fontWeight: 500,
                        height: WIKI_DETAIL.chipHeight,
                        '& .MuiChip-label': { fontSize: WIKI_DETAIL.chipFontSize, px: WIKI_DETAIL.chipPx }
                      }}
                    />
                  ))}
                </Box>
              </Box>
            )}

            {/* Cost Group */}
            {properties.filter(p => p.type === 'cost').length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: { xs: 1, sm: 1.5 }, fontWeight: 600, color: 'text.secondary', fontSize: WIKI_DETAIL.sectionTitleFontSize }}>
                  {t('skillSections.resourceConsumption')}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {properties.filter(p => p.type === 'cost').map((prop, index) => (
                    <Chip
                      key={index}
                      label={`${prop.label}: ${prop.value}`}
                      size="small"
                      sx={{
                        backgroundColor: BG_COLORS.GLASS,
                        color: 'text.primary',
                        fontWeight: 500,
                        height: WIKI_DETAIL.chipHeight,
                        '& .MuiChip-label': { fontSize: WIKI_DETAIL.chipFontSize, px: WIKI_DETAIL.chipPx }
                      }}
                    />
                  ))}
                </Box>
              </Box>
            )}

            {/* Learning Group */}
            {properties.filter(p => p.type === 'learning').length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: { xs: 1, sm: 1.5 }, fontWeight: 600, color: 'text.secondary', fontSize: WIKI_DETAIL.sectionTitleFontSize }}>
                  {t('skillSections.learning')}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {properties.filter(p => p.type === 'learning').map((prop, index) => (
                    <Chip
                      key={index}
                      label={`${prop.label}: ${prop.value}`}
                      size="small"
                      sx={{
                        backgroundColor: BG_COLORS.GLASS,
                        color: 'text.primary',
                        fontWeight: 500,
                        height: WIKI_DETAIL.chipHeight,
                        '& .MuiChip-label': { fontSize: WIKI_DETAIL.chipFontSize, px: WIKI_DETAIL.chipPx }
                      }}
                    />
                  ))}
                </Box>
              </Box>
            )}

            {/* Timing Group */}
            {properties.filter(p => p.type === 'timing').length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: { xs: 1, sm: 1.5 }, fontWeight: 600, color: 'text.secondary', fontSize: WIKI_DETAIL.sectionTitleFontSize }}>
                  {t('skillSections.timing')}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {properties.filter(p => p.type === 'timing').map((prop, index) => (
                    <Chip
                      key={index}
                      label={`${prop.label}: ${prop.value}`}
                      size="small"
                      sx={{
                        backgroundColor: BG_COLORS.GLASS,
                        color: 'text.primary',
                        fontWeight: 500,
                        height: WIKI_DETAIL.chipHeight,
                        '& .MuiChip-label': { fontSize: WIKI_DETAIL.chipFontSize, px: WIKI_DETAIL.chipPx }
                      }}
                    />
                  ))}
                </Box>
              </Box>
            )}

            {/* Boolean Features */}
            {properties.filter(p => p.type === 'boolean').length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: { xs: 1, sm: 1.5 }, fontWeight: 600, color: 'text.secondary', fontSize: WIKI_DETAIL.sectionTitleFontSize }}>
                  {t('detailSections.features')}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {properties.filter(p => p.type === 'boolean').map((prop, index) => (
                    <Chip
                      key={index}
                      label={`${prop.label}: ${prop.value}`}
                      size="small"
                      variant="outlined"
                      sx={{
                        borderColor: BORDER_COLORS.LIGHTER,
                        color: 'text.primary',
                        height: WIKI_DETAIL.chipHeight,
                        '& .MuiChip-label': { fontSize: WIKI_DETAIL.chipFontSize, px: WIKI_DETAIL.chipPx }
                      }}
                    />
                  ))}
                </Box>
              </Box>
            )}

            {/* Item Consumption */}
            {properties.filter(p => p.type === 'item').length > 0 && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: { xs: 1, sm: 1.5 }, fontWeight: 600, color: 'text.secondary', fontSize: WIKI_DETAIL.sectionTitleFontSize }}>
                  {t('skillSections.requiredItems')}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {properties.filter(p => p.type === 'item').map((prop, index) => (
                    <Chip
                      key={index}
                      label={`${prop.label}: ${prop.value}`}
                      size="small"
                      variant="outlined"
                      sx={{
                        borderColor: BORDER_COLORS.LIGHTER,
                        color: 'text.primary',
                        height: WIKI_DETAIL.chipHeight,
                        '& .MuiChip-label': { fontSize: WIKI_DETAIL.chipFontSize, px: WIKI_DETAIL.chipPx }
                      }}
                    />
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        )}
        </SimpleBar>
    </Box>
  );
};

export default SkillDetail;
